import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { tolerantId } from "../utils/firestoreHelpers";
import { formatCurrencyUY } from "../utils/formatters";
import { computeUnitsAccountStatus } from "../utils/unitAccountStatus";
import { useIsMobile } from "../hooks/useResponsive";
import { useBuilding } from "../context/BuildingContext";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaPercentage,
  FaFileInvoiceDollar,
  FaHome,
  FaBuilding,
  FaSpinner,
} from "react-icons/fa";

// Toolkit admin (desktop)
import KPICard from "./KPICard";
import DataTable from "./DataTable";
import StatusBadge from "./StatusBadge";
import EmptyState from "./EmptyState";
import { colors, spacing, radius, shadows, typography } from "../utils/designTokens";

// Toolkit app residente (mobile)
import { StatCard, SegmentedControl, ListItem, Badge, Money, AppEmptyState, AppCard } from "./app";
import { appColors, appSpacing, appType } from "../utils/appTheme";
import PaymentEvolutionChart from "./PaymentEvolutionChart";
import { useTranslation } from "react-i18next";

/**
 * Vista "El edificio" del Estado de Cuenta del residente.
 * Muestra, sin nombres, qué apartamentos están al día y cuáles en deuda.
 *
 * Props:
 *  - buildingId: buildingid del edificio seleccionado.
 *
 * En un edificio de solo lectura (ex-ocupante) no consulta nada y muestra un
 * aviso: las reglas 0D le dejan leer solo sus pagos, no los de todo el edificio.
 */
const BuildingAccountView = ({ buildingId }) => {
  const { t } = useTranslation("buildingAccount");
  const FILTER_OPTIONS = [
    { value: "todos", label: t("filter.all") },
    { value: "al_dia", label: t("filter.alDia") },
    { value: "en_deuda", label: t("filter.enDeuda") },
  ];
  const isMobile = useIsMobile();
  const { selectedBuilding } = useBuilding();
  const soloLectura =
    !!selectedBuilding?.isReadOnly && String(selectedBuilding?.buildingid) === String(buildingId);
  const [loading, setLoading] = useState(true);
  const [apartments, setApartments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [building, setBuilding] = useState(null);
  const [filter, setFilter] = useState("todos");

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!buildingId || soloLectura) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // FIN-053: los payments usan "buildingid" (estándar) y legacy "buildingId"
        // (camelCase). Se hacen 2 queries y se deduplican por doc ID.
        const [aptSnap, paySnap, paySnap2, bldSnap] = await Promise.all([
          getDocs(query(collection(db, "apartments"), where("building", "in", tolerantId(buildingId)))),
          getDocs(query(collection(db, "payments"), where("buildingid", "in", tolerantId(buildingId)))),
          getDocs(query(collection(db, "payments"), where("buildingId", "in", tolerantId(buildingId)))),
          getDocs(query(collection(db, "building"), where("buildingid", "in", tolerantId(buildingId)))),
        ]);
        if (cancelled) return;

        const apts = aptSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const payMap = new Map();
        paySnap.docs.forEach((d) => payMap.set(d.id, { id: d.id, ...d.data() }));
        paySnap2.docs.forEach((d) => { if (!payMap.has(d.id)) payMap.set(d.id, { id: d.id, ...d.data() }); });
        // Solo pagos de cierre de mes (montlyaccountId) o migración (isMigration),
        // igual que Cobranza.
        const pays = Array.from(payMap.values()).filter((p) => p.montlyaccountId || p.isMigration);

        setApartments(apts);
        setPayments(pays);
        setBuilding(bldSnap.docs[0]?.data() || null);
      } catch (err) {
        console.error("Error cargando estado de cuenta del edificio:", err);
        if (!cancelled) {
          setApartments([]);
          setPayments([]);
          setBuilding(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [buildingId, soloLectura]);

  const { units, stats } = useMemo(
    () => computeUnitsAccountStatus({ apartments, payments, building }),
    [apartments, payments, building]
  );

  const filteredUnits = useMemo(
    () => units.filter((u) => (filter === "todos" ? true : u.estado === filter)),
    [units, filter]
  );

  // ─── Solo lectura (ex-ocupante) ─────────────────────────────────────────
  if (soloLectura) {
    const titulo = t("readOnly.title", { defaultValue: "Vista no disponible" });
    const detalle = t("readOnly.desc", {
      defaultValue:
        "Tu vínculo con este edificio terminó: podés ver tus pagos, pero no el estado de todas las unidades.",
    });
    return isMobile ? (
      <AppEmptyState icon={<FaBuilding />} title={titulo} description={detalle} />
    ) : (
      <EmptyState icon={FaBuilding} title={titulo} description={detalle} />
    );
  }

  // ─── Loading ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          padding: "48px 16px",
          color: isMobile ? appColors.ink3 : colors.gray400,
          fontSize: 13,
        }}
      >
        <FaSpinner className="spin" size={14} />
        {t("loading")}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <div>
        {/* KPIs */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: appSpacing.gridGap,
            marginBottom: appSpacing.section,
          }}
        >
          <StatCard
            icon={<FaCheckCircle />}
            label={t("kpi.alDia")}
            value={stats.unitsAlDia}
            valueColor={appColors.green}
            sub={t("sub.ofUnits", { count: stats.totalUnits })}
            style={{ minWidth: 0 }}
          />
          <StatCard
            icon={<FaExclamationTriangle />}
            label={t("kpi.enDeuda")}
            value={stats.unitsInDebt}
            valueColor={stats.unitsInDebt > 0 ? appColors.red : appColors.ink}
            sub={t("sub.ofUnits", { count: stats.totalUnits })}
            style={{ minWidth: 0 }}
          />
          <StatCard
            variant="cream"
            icon={<FaPercentage />}
            label={t("kpi.morosidad")}
            value={`${stats.morosidad}%`}
            valueColor={stats.morosidad > 0 ? appColors.amber : appColors.ink}
            sub={t("sub.overdueUnits")}
            style={{ minWidth: 0 }}
          />
          <StatCard
            variant="cream"
            icon={<FaFileInvoiceDollar />}
            label={t("kpi.deudaTotal")}
            value={<Money value={stats.totalDebt} size={18} tone={stats.totalDebt > 0 ? "red" : undefined} />}
            valueColor={stats.totalDebt > 0 ? appColors.red : appColors.ink}
            sub={t("sub.ofBuilding")}
            style={{ minWidth: 0 }}
          />
        </div>

        {/* Evolución de pagos del edificio */}
        <AppCard style={{ marginBottom: appSpacing.section }} padding={18}>
          <PaymentEvolutionChart payments={payments} initialMonths={6} />
        </AppCard>

        {/* Filtro */}
        <SegmentedControl
          options={FILTER_OPTIONS}
          value={filter}
          onChange={setFilter}
          style={{ marginBottom: appSpacing.section }}
        />

        {/* Lista */}
        {filteredUnits.length === 0 ? (
          <AppEmptyState
            icon={<FaBuilding />}
            title={t("empty.title")}
            description={t("empty.desc")}
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {filteredUnits.map((u) => {
              const enDeuda = u.estado === "en_deuda";
              return (
                <ListItem
                  key={u.unitId}
                  variant="card"
                  icon={<FaHome />}
                  title={t("unit", { number: u.unitNumber })}
                  subtitle={
                    enDeuda && u.maxOverdue > 0 ? t("overdueDays", { count: u.maxOverdue }) : undefined
                  }
                  trailing={
                    <span
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 6,
                        minWidth: 0,
                      }}
                    >
                      <Badge tone={enDeuda ? "red" : "green"} dot>
                        {enDeuda ? t("status.enDeuda") : t("status.alDia")}
                      </Badge>
                      {enDeuda ? (
                        <Money value={u.totalDebt} size={15} tone="red" />
                      ) : (
                        <span style={{ ...appType.bodySm, color: appColors.ink3 }}>—</span>
                      )}
                    </span>
                  }
                />
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP
  // ══════════════════════════════════════════════════════════════════════════
  const columns = [
    {
      key: "unitNumber",
      label: t("columns.unit"),
      render: (v) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: colors.black }}>
          <FaHome style={{ color: colors.gray400, fontSize: 13 }} />
          {t("unit", { number: v })}
        </span>
      ),
    },
    {
      key: "estado",
      label: t("columns.status"),
      render: (v) => <StatusBadge status={v} label={v === "en_deuda" ? t("status.enDeuda") : t("status.alDia")} />,
    },
    {
      key: "maxOverdue",
      label: t("columns.overdue"),
      render: (v, row) =>
        row.estado === "en_deuda" && v > 0 ? (
          <span style={{ color: colors.gray600, fontSize: typography.sizes.base }}>{t("days", { count: v })}</span>
        ) : (
          <span style={{ color: colors.gray400 }}>—</span>
        ),
    },
    {
      key: "totalDebt",
      label: t("columns.debt"),
      align: "right",
      render: (v, row) =>
        row.estado === "en_deuda" ? (
          <span style={{ color: colors.error }}>{formatCurrencyUY(v)}</span>
        ) : (
          <span style={{ color: colors.gray400 }}>—</span>
        ),
    },
  ];

  const tableData = filteredUnits.map((u) => ({ ...u, id: u.unitId }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: spacing[6] }}>
      {/* KPIs */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: spacing[4],
        }}
      >
        <KPICard
          icon={FaCheckCircle}
          iconBg={colors.successLight}
          iconColor={colors.success}
          label={t("kpi.alDia")}
          value={stats.unitsAlDia}
        />
        <KPICard
          icon={FaExclamationTriangle}
          iconBg={colors.errorLight}
          iconColor={colors.error}
          label={t("kpi.enDeuda")}
          value={stats.unitsInDebt}
        />
        <KPICard
          icon={FaPercentage}
          iconBg={colors.warningLight}
          iconColor={colors.warning}
          label={t("kpi.morosidad")}
          value={`${stats.morosidad}%`}
        />
        <KPICard
          icon={FaFileInvoiceDollar}
          iconBg={colors.primaryLighter}
          iconColor={colors.primary}
          label={t("kpi.deudaTotal")}
          value={formatCurrencyUY(stats.totalDebt)}
        />
      </div>

      {/* Evolución de pagos del edificio */}
      <div
        style={{
          background: colors.bgCard,
          borderRadius: radius.md,
          padding: spacing[5],
          boxShadow: shadows.sm,
        }}
      >
        <PaymentEvolutionChart payments={payments} initialMonths={6} />
      </div>

      {/* Filtro + tabla */}
      <div
        style={{
          background: colors.bgCard,
          borderRadius: radius.md,
          padding: spacing[5],
          boxShadow: shadows.sm,
          display: "flex",
          flexDirection: "column",
          gap: spacing[4],
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: spacing[3], flexWrap: "wrap" }}>
          <h2 style={{ fontSize: typography.sizes.lg, fontWeight: 400, color: colors.black, margin: 0 }}>
            {t("sectionTitle")}
          </h2>
          <div style={{ maxWidth: 320, flex: "1 1 240px" }}>
            <SegmentedControl options={FILTER_OPTIONS} value={filter} onChange={setFilter} />
          </div>
        </div>

        <DataTable
          columns={columns}
          data={tableData}
          emptyIcon={FaBuilding}
          emptyTitle={t("empty.title")}
          emptyDescription={t("empty.desc")}
        />
      </div>
    </div>
  );
};

export default BuildingAccountView;
